import React, { useState, FC } from 'react'
import { TextField, Snackbar } from '@mui/material'
import ButtonWrapper from 'components/custom/ButtonWrapper'
import PaperWrapper from 'components/custom/PaperWrapper'
import ContentBodyWrapper from 'components/custom/ContentBodyWrapper'
import { SustamizeSpinner } from '../Interpreter/components/sustamizeSpinner/sustamizeSpinner'
import CollapsibleLcaTable from './components/CollapsibleLcaTable'
import { ModalNewName } from 'pages/Assembler/components/AssemblerModals'
import { eColor } from 'components/types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { sagaCreateAssemblyAction } from 'saga/actions/assembler'
import { RoutePaths } from 'routes/paths'
import { componentPermissions } from 'config/permissions'
import { componentNames } from 'config/roles'

const LcaGenerator: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [input, setInput] = useState('')
  const [modelJson, setModelJson] = useState<string>('')
  const [parsedModel, setParsedModel] = useState<any>(null)
  const [loadingModel, setLoadingModel] = useState(false)
  const [loadingInterpret, setLoadingInterpret] = useState(false)
  const [interpretedResult, setInterpretedResult] = useState<any>(null)
  const [openTransferModal, setOpenTransferModal] = useState(false)
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false)

  const BEARER_TOKEN = localStorage.getItem('access_token')

  const handleGenerate = async () => {
    if (!input.trim()) return
    setLoadingModel(true)

    try {
      const res = await fetch(
        'http://localhost:8000/lca-generator/generate-model',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${BEARER_TOKEN}`,
          },
          body: JSON.stringify({ customInput: input.trim() }),
        }
      )

      const data = await res.json()
      setParsedModel(data)
      setModelJson(JSON.stringify(data, null, 2))
    } catch (error) {
      alert('Failed to fetch model.')
    } finally {
      setLoadingModel(false)
    }
  }

  const handleInterpret = async () => {
    try {
      const model = JSON.parse(modelJson)
      setParsedModel(model)
      setLoadingInterpret(true)

      const res = await fetch('http://localhost:8000/lca-generator/interpret', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${BEARER_TOKEN}`,
        },
        body: JSON.stringify(model),
      })

      const data = await res.json()
      setInterpretedResult(data)
      setOpenTransferModal(true)
    } catch (error) {
      alert('Failed to interpret model. Check JSON formatting.')
    } finally {
      setLoadingInterpret(false)
    }
  }

  const handleTransfer = (name: string) => {
    const processChain = interpretedResult?.calculationProcessChain || []
    dispatch(
      sagaCreateAssemblyAction({
        name: name,
        calculationProcessChain: processChain,
      })
    )
    setShowSuccessSnackbar(true)
  }

  return (
    <ContentBodyWrapper title={t('lcaBuilder.title', 'LCA Builder')}>
      <PaperWrapper className="p-6 space-y-6">
        {/* INPUT FIELD */}
        <div className="flex gap-2">
          <TextField
            label={t('lcaBuilder.inputLabel', 'Custom Input')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            fullWidth
          />
          <ButtonWrapper
            onClick={handleGenerate}
            disabled={!input || loadingModel}
            color={eColor.primary}
          >
            {t('lcaBuilder.generate', 'Generate Model')}
          </ButtonWrapper>
        </div>

        {/* LOADING SPINNER */}
        {loadingModel && (
          <div className="flex flex-col items-center justify-center py-12">
            <SustamizeSpinner />{' '}
          </div>
        )}

        {/* COLLAPSIBLE + JSON + ACTIONS */}
        {parsedModel && (
          <>
            <CollapsibleLcaTable data={parsedModel.calculationSteps} />

            <TextField
              label={t('lcaBuilder.jsonLabel', 'Editable JSON')}
              multiline
              rows={10}
              fullWidth
              value={modelJson}
              onChange={(e) => setModelJson(e.target.value)}
              variant="outlined"
            />

            <div className="flex flex-wrap gap-4">
              <ButtonWrapper
                onClick={handleInterpret}
                disabled={loadingInterpret}
                color={eColor.primary}
              >
                {t('lcaBuilder.interpret', 'Interpret Model')}
              </ButtonWrapper>

              <ButtonWrapper
                onClick={() => setOpenTransferModal(true)}
                disabled={!interpretedResult}
                color={eColor.primary}
              >
                {t('lcaBuilder.transferToAssembler', 'Transfer to Assembler')}
              </ButtonWrapper>
            </div>
          </>
        )}

        {/* LOADING SPINNER */}
        {loadingInterpret && (
          <div className="flex flex-col items-center justify-center py-12">
            <SustamizeSpinner />{' '}
          </div>
        )}

        {/* MODAL: NEW ASSEMBLY NAME */}
        <ModalNewName
          title={t('lcaBuilder.newAssembly', 'Create New Assembly')}
          message={t('lcaBuilder.namePrompt', 'Enter a name:')}
          open={openTransferModal}
          handleClose={() => setOpenTransferModal(false)}
          yesOption={(name) => {
            handleTransfer(name)
            setOpenTransferModal(false)
          }}
          existingNames={[]}
        />

        {/* SNACKBAR */}
        <Snackbar
          open={showSuccessSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSuccessSnackbar(false)}
          message={t('lcaBuilder.success', 'Assembly created successfully!')}
        />
      </PaperWrapper>
    </ContentBodyWrapper>
  )
}

export default {
  routeProps: {
    path: RoutePaths.toolboxLcaGenerator,
    title: 'toolbox.lcaGenerator',
    element: <LcaGenerator />,
    roles: componentPermissions[componentNames.pageLcaGenerator],
    requiresAuthentication: true,
  },
  name: 'Standard Parts',
}
